import { Box, Card, CardContent, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useApiContext } from '@/contexts/apiContext';
import MultiSelectFree from '@/components/muiltipleSelectFree';
import HubIcon from '@mui/icons-material/Hub';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BoltIcon from '@mui/icons-material/Bolt';
import BuildIcon from '@mui/icons-material/Build';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import { useUserContext } from '@/contexts/userContext';

interface Props {
  entity: any;
  onChange: (settingName: string, value: any) => void;
  availableAgents?: any[];
}

export const AgentExecutionModeSettings = ({ entity, onChange, availableAgents }: Props) => {
  const userContext = useUserContext();
  const apiContext = useApiContext();
  const currentMode = entity.settings?.executionMode || 'standard';
  const whitelistedTools: string[] = entity.settings?.whitelistedTools || [];
  const delegatedAgentIds: string[] = entity.settings?.delegatedAgentIds || [];

  const [mcpTools, setMcpTools] = useState<Array<{ name: string; description?: string }>>([]);
  const [loadingTools, setLoadingTools] = useState<boolean>(false);
  const [agentList, setAgentList] = useState<any[]>(availableAgents || []);
  const [loadingAgents, setLoadingAgents] = useState<boolean>(false);

  // Dynamically load registered MCP tools from MCP Server via getMcpTools()
  useEffect(() => {
    let isMounted = true;
    const fetchTools = async () => {
      setLoadingTools(true);
      try {
        const timeoutPromise = new Promise<any[]>((_, reject) =>
          setTimeout(() => reject(new Error('MCP tools discovery timed out')), 5000),
        );
        const targetDomainId = (entity as any)?.domainId || userContext?.user?.domainId;
        const tools = await Promise.race([
          apiContext.getMcpTools(undefined, undefined, undefined, targetDomainId),
          timeoutPromise,
        ]);
        if (isMounted && Array.isArray(tools)) {
          setMcpTools(tools);
        }
      } catch (err) {
        console.warn('MCP tools discovery via getMcpTools failed:', err);
      } finally {
        if (isMounted) {
          setLoadingTools(false);
        }
      }
    };
    fetchTools();
    return () => {
      isMounted = false;
    };
  }, []);

  // Dynamically load available agent instances and definitions to populate delegation targets
  useEffect(() => {
    let isMounted = true;
    const fetchAgents = async () => {
      setLoadingAgents(true);
      try {
        const timeoutPromise = new Promise<any[]>((_, reject) =>
          setTimeout(() => reject(new Error('Agent discovery timed out')), 5000),
        );

        // Fetch both agent instances and agent definitions in parallel
        const [instancesRes, defsRes] = await Promise.allSettled([
          Promise.race([apiContext.getAgentInstances(false, false), timeoutPromise]),
          Promise.race([apiContext.getAgentDefs(), timeoutPromise]),
        ]);

        const instances =
          instancesRes.status === 'fulfilled' && Array.isArray(instancesRes.value) ? instancesRes.value : [];
        const defs = defsRes.status === 'fulfilled' && Array.isArray(defsRes.value) ? defsRes.value : [];

        if (isMounted) {
          const map = new Map<string, any>();

          // 1. Add any availableAgents passed from canvas
          if (Array.isArray(availableAgents)) {
            availableAgents.forEach((ag: any) => {
              if (ag && ag.id) {
                map.set(ag.id, {
                  id: ag.id,
                  name: ag.name || ag.agentDef?.name || ag.id,
                  agentDef: ag.agentDef,
                  description: ag.description || ag.agentDef?.description || 'Pipeline Agent',
                });
              }
            });
          }

          // 2. Add tenant agent instances
          instances.forEach((inst: any) => {
            if (inst && inst.id) {
              map.set(inst.id, {
                id: inst.id,
                name: inst.name || inst.agentDef?.name || inst.id,
                agentDef: inst.agentDef,
                description: inst.description || inst.agentDef?.description || 'Agent Instance',
              });
            }
          });

          // 3. Add agent definitions so users can always delegate to standard agent types
          defs.forEach((def: any) => {
            if (def && def.id && !map.has(def.id)) {
              map.set(def.id, {
                id: def.id,
                name: def.name || def.id,
                agentDef: def,
                description: def.description || 'Agent Definition',
              });
            }
          });

          // Filter out current agent (by id or defId)
          const currentId = entity?.id;
          const currentDefId = entity?.entityDefId || entity?.agentDefId || entity?.agentDef?.id;
          const filtered = Array.from(map.values()).filter((ag: any) => ag.id !== currentId && ag.id !== currentDefId);

          setAgentList(filtered);
        }
      } catch (err) {
        console.warn('Failed to load agent targets', err);
      } finally {
        if (isMounted) {
          setLoadingAgents(false);
        }
      }
    };

    fetchAgents();
    return () => {
      isMounted = false;
    };
  }, [entity?.id, entity?.entityDefId, entity?.agentDefId, availableAgents]);

  const toolDescriptionMap = useMemo(() => {
    const map = new Map<string, string>();
    mcpTools.forEach((t) => {
      if (t?.name && t?.description) {
        map.set(t.name, t.description);
      }
    });
    return map;
  }, [mcpTools]);

  const availableToolNames = useMemo(() => {
    const names = new Set<string>();
    // Tools discovered dynamically from getMcpTools()
    mcpTools.forEach((t) => {
      if (t?.name) {
        names.add(t.name);
      }
    });
    // Preserve any tools previously whitelisted on this entity
    whitelistedTools.forEach((t) => {
      if (t) {
        names.add(t);
      }
    });
    return Array.from(names);
  }, [mcpTools, whitelistedTools]);

  const allAgents = useMemo(() => {
    const list = [...agentList];
    delegatedAgentIds.forEach((agentId) => {
      if (!list.some((a) => a.id === agentId)) {
        list.push({ id: agentId, name: `Agent (${agentId})` });
      }
    });
    return list;
  }, [agentList, delegatedAgentIds]);

  const agentMap = useMemo(() => {
    const map: Record<string, any> = {};
    allAgents.forEach((ag) => {
      map[ag.id] = ag;
    });
    return map;
  }, [allAgents]);

  const availableAgentIds = useMemo(() => {
    return allAgents.map((ag) => ag.id);
  }, [allAgents]);

  const getAgentOptionLabel = (option: string) => {
    const ag = agentMap[option];
    if (!ag) return option;
    return ag.name ? `${ag.name} (${ag.id})` : ag.id;
  };

  const getToolDescription = (toolName: string) => {
    return toolDescriptionMap.get(toolName);
  };

  const handleModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('executionMode', e.target.value);
  };

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: 'text.primary' }}>
        Agent Execution Mode
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        Select how this agent executes within the pipeline workflow.
      </Typography>

      <RadioGroup value={currentMode} onChange={handleModeChange}>
        {/* Standard Mode */}
        <Card
          variant="outlined"
          sx={{
            mb: 1.5,
            borderRadius: '10px',
            borderColor: currentMode === 'standard' ? '#2563eb' : 'divider',
            bgcolor: currentMode === 'standard' ? '#eff6ff' : '#ffffff',
            cursor: 'pointer',
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <FormControlLabel
              value="standard"
              control={<Radio size="small" />}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BoltIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                    Standard Pipeline Step
                  </Typography>
                </Stack>
              }
              sx={{ m: 0, width: '100%' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 4, mt: 0.25 }}>
              Deterministic single-pass transformation. Ideal for simple ingestion, scoring, and legacy workflows.
            </Typography>
          </CardContent>
        </Card>

        {/* Autonomous ReAct */}
        <Card
          variant="outlined"
          sx={{
            mb: 1.5,
            borderRadius: '10px',
            borderColor: currentMode === 'react' ? '#7c3aed' : 'divider',
            bgcolor: currentMode === 'react' ? '#faf5ff' : '#ffffff',
            cursor: 'pointer',
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <FormControlLabel
              value="react"
              control={<Radio size="small" color="secondary" />}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PsychologyIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#581c87' }}>
                    Autonomous ReAct (Reasoning & Acting)
                  </Typography>
                </Stack>
              }
              sx={{ m: 0, width: '100%' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 4, mt: 0.25 }}>
              Iterative Thought ➔ Action ➔ Observation loop. Solves complex tasks using authorized MCP tools.
            </Typography>
          </CardContent>
        </Card>

        {/* Swarm Supervisor */}
        <Card
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: '10px',
            borderColor: currentMode === 'swarm_supervisor' ? '#9333ea' : 'divider',
            bgcolor: currentMode === 'swarm_supervisor' ? '#fdf4ff' : '#ffffff',
            cursor: 'pointer',
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <FormControlLabel
              value="swarm_supervisor"
              control={<Radio size="small" sx={{ color: '#9333ea', '&.Mui-checked': { color: '#9333ea' } }} />}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <HubIcon sx={{ fontSize: 16, color: '#9333ea' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#701a75' }}>
                    Dynamic Swarm Supervisor
                  </Typography>
                </Stack>
              }
              sx={{ m: 0, width: '100%' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 4, mt: 0.25 }}>
              Decomposes complex goals and dynamically delegates sub-tasks across peer specialist agents in parallel.
            </Typography>
          </CardContent>
        </Card>
      </RadioGroup>

      {/* Tool Whitelisting Drawer Section */}
      {(currentMode === 'react' || currentMode === 'swarm_supervisor') && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <BuildIcon sx={{ fontSize: 16, color: '#0284c7' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Authorized MCP Tools
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Restrict which external tools this agent is permitted to execute during its reasoning loop. Type to add
            custom tools or select from discovered MCP tools.
          </Typography>

          <MultiSelectFree
            label=""
            placeholder="Search tools by name, description, or type custom..."
            value={whitelistedTools}
            options={availableToolNames}
            loading={loadingTools}
            loadingText="Loading MCP tools..."
            getOptionDescription={getToolDescription}
            onChange={(newTools) => onChange('whitelistedTools', newTools)}
          />
        </Box>
      )}

      {/* Peer Agent Delegation Whitelist for Swarm Supervisor */}
      {currentMode === 'swarm_supervisor' && (
        <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid #e2e8f0' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <GroupWorkIcon sx={{ fontSize: 16, color: '#9333ea' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Peer Agent Delegation Targets
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Authorize specialist agents this supervisor can invoke via runtime delegation edges. Type to search or
            select agents.
          </Typography>

          <MultiSelectFree
            label=""
            placeholder="Search agents by name, role, or ID..."
            value={delegatedAgentIds}
            options={availableAgentIds}
            loading={loadingAgents}
            loadingText="Loading available agents..."
            getOptionLabel={getAgentOptionLabel}
            getOptionDescription={(id) => agentMap[id]?.description}
            onChange={(newAgentIds) => onChange('delegatedAgentIds', newAgentIds)}
          />
        </Box>
      )}
    </Box>
  );
};

export default AgentExecutionModeSettings;
